// Helpers de fecha del panel "Movimientos Futuros" de FlujoCajaV2 (colapso de vencidos, sesión
// 2026-09-07, ver docs/decisiones/sesion-2026-09-07-futuros-colapso-vencidos.md y regla 10.244).
// Extraídos tal cual de src/components/FlujoCajaV2/index.js (Fase 0 de "Futuros: reagendar",
// 2026-09-23) — mismo comportamiento, sin cambios de lógica. Se dejan acá (y no en un util más
// genérico de fechas) porque siguen siendo feature-local: el `toLocalYMD`/día-hábil de este archivo
// es el criterio LOCAL del panel de futuros, distinto del `hoyStr` global (UTC) que usa el resto de
// FlujoCajaV2 — unificar esas dos nociones de "hoy" es un cambio de comportamiento fuera de alcance
// (ver ROADMAP_TECNICO, ítem heredado de la sesión 2026-09-07).

// Fecha LOCAL en "YYYY-MM-DD" (getFullYear/getMonth/getDate), NO UTC. Clave para los rangos de fecha
// del histórico: toISOString() serializa en UTC y de tarde/noche en Chile/Perú (UTC−4/−5) apunta al
// día SIGUIENTE, dejando "Hoy" sin matchear los movimientos de la fecha local. Ancla a la zona de la
// máquina del usuario (correcto mientras esté en la zona del país operativo, el caso normal).
export const toLocalYMD = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// ¿"YYYY-MM-DD" cae de lunes a viernes? (Fase 1c de "Futuros: reagendar" — bloqueo de fin de semana,
// Commit 3). Fuente ÚNICA de "qué es día hábil" para este panel: `proximoDiaHabil` la reusa
// internamente (abajo) y el bloqueo de fin de semana en `CeldaFechaReagendable`/`reagendarEntryFuturo`
// también — un solo criterio, nunca dos lógicas de día-hábil que puedan divergir. El proyecto NO tiene
// calendario de feriados (mismo alcance que `proximoDiaHabil` ya documentaba): SOLO sábado/domingo.
export const esDiaHabil = (ymd) => {
  const d = parseLocalYMD(ymd);
  if (!d) return false;
  const dow = d.getDay(); // dom=0 … sáb=6
  return dow !== 0 && dow !== 6;
};

// Próximo día hábil como string LOCAL "YYYY-MM-DD". SOLO corre fin de semana (sábado/domingo → lunes):
// el proyecto NO tiene calendario de feriados, así que no se inventa uno (mismo criterio que la lógica
// de día hábil ya existente en fechaPagoSueldo.js / AgendarMovimientoModal). Un día hábil devuelve su
// propia fecha. Se deja local en este componente a propósito: la usa el colapso de vencidos del panel
// de futuros; extraerla a un util genérico (y unificar las copias de esa lógica) es fuera de alcance acá.
// Reusa `esDiaHabil` (Fase 1c) — mismo criterio, no una segunda copia de "qué día es fin de semana".
export const proximoDiaHabil = (fecha) => {
  const d = new Date(fecha);
  if (!esDiaHabil(toLocalYMD(d))) {
    const dow = d.getDay(); // dom=0 … sáb=6
    if (dow === 6) {
      d.setDate(d.getDate() + 2); // sábado → lunes
    } else if (dow === 0) {
      d.setDate(d.getDate() + 1); // domingo → lunes
    }
  }
  return toLocalYMD(d);
};

// "28 ago" para una fecha "YYYY-MM-DD": mismo Intl toLocaleDateString("es", …) que ya usa el panel
// (labelMes), sin tabla de meses propia. Parseo en fecha LOCAL (new Date(y, m-1, d)) para no correrse
// un día por UTC. El .replace saca el punto que algunos locales agregan ("sept." → "sept").
export const formatFechaCorta = (fechaStr) => {
  if (!fechaStr) return "";
  const [y, m, d] = fechaStr.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("es", { day: "numeric", month: "short" }).replace(".", "");
};

// Días calendario vencidos entre la fecha comprometida original y HOY, ambos a medianoche. "Hoy" =
// huso REAL del país operativo (`hoyDelPais(pais)`, Fase 1c — reemplaza `new Date()` del huso del
// NAVEGADOR, que podía no coincidir con el país de la fila y correr el conteo un día). `pais` se
// propaga en cascada desde el único call site real (`tituloVencida` → `FlujoCajaV2/index.js`, la
// fila que se está pintando). Sin `pais` cae al huso del navegador (mismo fallback degradado y
// logueado que ya usa `hoyDelPais`, 10.177) — nunca revienta, pero el caso real siempre pasa `pais`.
export const diasVencida = (fechaOriginalStr, pais) => {
  if (!fechaOriginalStr) return 0;
  const [y, m, d] = fechaOriginalStr.slice(0, 10).split("-").map(Number);
  const orig = new Date(y, m - 1, d);
  const hoy = parseLocalYMD(hoyDelPais(pais));
  // Math.round, NO Math.floor (Fase 1d): el día que Chile adelanta el reloj por horario de verano, el
  // intervalo entre dos medianoches locales mide 23 horas reales, no 24 — con Math.floor eso recorta
  // un día completo del conteo justo ese día (23h/86400000 = 0.958..., floor lo tira a 0 menos de lo
  // esperado). Math.round absorbe esa media hora/hora de diferencia sin alterar el resultado en el
  // caso normal (ambas fechas ya vienen a medianoche LOCAL, así que el resto siempre es ~0 salvo por
  // el corrimiento de DST).
  return Math.round((hoy - orig) / 86400000);
};

// Texto del hover de un vencido: "Comprometida el 28 ago · vencida hace 13 días" (singular "1 día").
export const tituloVencida = (fechaOriginalStr, pais) => {
  const dias = diasVencida(fechaOriginalStr, pais);
  return `Comprometida el ${formatFechaCorta(fechaOriginalStr)} · vencida hace ${dias} ${dias === 1 ? "día" : "días"}`;
};

// Predicado ÚNICO de "¿esta cuota tiene su fecha modificada a mano respecto de la ORIGINAL del plan?"
// (Fase 3c de "Futuros: reagendar", 2026-09-25, regla de negocio definida por Ignacio) — fuente única
// para los 5 mappers de cuota de FlujoCajaV2/index.js (cobro, pago SII recibida/externa/boleta, CapEx).
// `fechaOriginal` es `fecha_vencimiento_original` (columna nullable, poblada por el trigger
// `capturar_fecha_vencimiento_original`, migración 20260925b APLICADA 2026-09-25) — `null`/ausente
// significa "nunca se movió desde que se armó el plan" → sin color. El trigger NUNCA resetea el
// original a `null` una vez capturado (es la fecha real que tenía el plan, no se "olvida"); por eso,
// si el usuario reagenda la cuota DE VUELTA a esa fecha original, `fecha_vencimiento_original` sigue
// poblada pero ya NO debe verse azul — el override lo decide la comparación contra la fecha ACTUAL,
// no solo la existencia del dato. `.slice(0,10)` defensivo (mismo criterio que `diasVencida`/
// `formatFechaCorta` en este archivo): ambos campos son `date` de Postgres (ya "YYYY-MM-DD" planos),
// pero nunca se asume el formato de lo que llega de la red.
export const esFechaModificada = (fechaOriginal, fechaActual) => {
  if (!fechaOriginal) return false;
  return fechaOriginal.slice(0, 10) !== String(fechaActual || "").slice(0, 10);
};

// "28/08/2026" para una fecha "YYYY-MM-DD" — DD/MM/YYYY explícito (mismo parseo manual que el resto de
// este archivo, sin `toLocaleDateString`). Usado por `tituloOverride`, abajo.
export const formatFechaLarga = (fechaStr) => {
  if (!fechaStr) return "";
  const [y, m, d] = fechaStr.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

// Texto del hover de la pill AZUL (override): "Fecha original: DD/MM/YYYY" cuando se conoce la fecha
// original del plan (`f.fecha_original_plan`, agregada por los 5 mappers de cuota); fallback al texto
// genérico que ya existía para las filas SIN cuota (plazocobro, pago_recurrente_fecha, etc. — esas
// siguen con su criterio de override actual, sin `fecha_original_plan`, sin cambios de esta fase).
export const tituloOverride = (fechaOriginalPlanStr) =>
  fechaOriginalPlanStr ? `Fecha original: ${formatFechaLarga(fechaOriginalPlanStr)}` : "Fecha modificada a mano";

// Parseo inverso de toLocalYMD: "YYYY-MM-DD" → Date LOCAL a medianoche (new Date(y, m-1, d), NO
// new Date(str) que interpreta el string como UTC y puede correrse un día). Mismo parseo que ya usan
// formatFechaCorta/diasVencida de arriba, factorizado acá para el calendario de reagendado (Fase 1 de
// "Futuros: reagendar", 2026-09-23): precarga el datepicker con la fecha comprometida actual.
export const parseLocalYMD = (fechaStr) => {
  if (!fechaStr) return null;
  const [y, m, d] = fechaStr.slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

/**
 * Fuente única (10.1/10.27) para extraer { mes (0-indexado, como Date.getMonth()), ano } de una
 * fecha de negocio "YYYY-MM-DD" (con o sin hora) SIN el bug de huso horario de `new
 * Date(fechaStr).getMonth()/.getFullYear()` — ese parseo interpreta el string como UTC medianoche,
 * y en husos negativos (Chile, Perú) el día 1 de cualquier mes cae en el mes/año ANTERIOR (H7,
 * reingeniería del EBITDA, Fase 2a, 2026-10-03). Reusa `parseLocalYMD` (mismo criterio que ya usan
 * FlujoCajaV2/VentasFacturas/GestionActivos) en vez de reimplementar el split a mano.
 * @param {string} fechaStr
 * @returns {{mes:number, ano:number}|null} null si la fecha es inválida/vacía
 */
export const mesYAnoDeFecha = (fechaStr) => {
  const d = parseLocalYMD(fechaStr);
  if (!d) return null;
  return { mes: d.getMonth(), ano: d.getFullYear() };
};

// Huso horario REAL del país operativo — fuente única (10.1/10.27; no existía un mapa país→huso en
// src/utils/ antes de esto, grep 2026-09-23) para "qué día es HOY" en el flujo de reagendado (Fase
// 1b de "Futuros: reagendar", revisión de Cowork). Formato de `pais` = "Chile"/"Perú" (regla 10.53),
// el mismo que ya usa `ctxReagendarFuturo` en FlujoCajaV2/index.js — NO el código de 3 letras de
// `getPaisCode.js` (esa fuente sirve a otras tablas, ver su cabecera; acá no aplica).
const HUSO_POR_PAIS = {
  Chile: "America/Santiago",
  Perú: "America/Lima",
};

/**
 * "YYYY-MM-DD" del día calendario ACTUAL en el huso del país operativo (Chile → America/Santiago,
 * Perú → America/Lima) — reemplaza el piso `toLocalYMD(new Date())` (huso del NAVEGADOR del
 * usuario, que puede no coincidir con el país de la fila que se reagenda) en los 3 puntos del flujo
 * de reagendado: el validador de `reagendarEntryFuturo`, el `minDate`/`filterDate` del calendario de
 * `CeldaFechaReagendable`, y el colapso de vencidos de `FlujoCajaV2/index.js`.
 *
 * `Intl.DateTimeFormat("en-CA", { timeZone, ... })` devuelve directamente "YYYY-MM-DD" (el locale
 * en-CA usa ese orden) — verificado con Node contra instantes reales cerca de medianoche, ver
 * `src/utils/__tests__/fechasFuturosPanel.test.js`.
 *
 * Ante país desconocido (no está en `HUSO_POR_PAIS`), cae al huso del navegador — el comportamiento
 * que existía en TODO el flujo antes de esta función — y lo loguea explícitamente (10.177: degradar
 * visible, nunca reventar el reagendado ni fallar en silencio).
 * @param {string} pais - "Chile" | "Perú".
 * @returns {string} "YYYY-MM-DD"
 */
export const hoyDelPais = (pais) => {
  const timeZone = HUSO_POR_PAIS[pais];
  if (!timeZone) {
    // eslint-disable-next-line no-console
    console.error(`hoyDelPais: país desconocido ${JSON.stringify(pais)} — uso el huso del navegador como fallback.`);
    return toLocalYMD(new Date());
  }
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
};
